import { z } from 'zod';
import { endpoint } from './common';

// The business dashboard: what people did in the app over the last few days, from the business events in Athena. Only the
// people the server lists in METRICS_DASHBOARD_EMAILS may ask for it.

export const DASHBOARD_DAYS = [7, 14, 30, 90] as const;

// This period and the equally long one before it.
const change = z.object({ current: z.number(), previous: z.number() });

export const dashboard = z.object({
    days: z.number(),
    // Days are UTC days. `through` is the last day shown (today).
    through: z.string(),
    generatedAt: z.string(),
    kpis: z.object({
        signUps: change,
        activePeople: change,
        tasksCreated: change,
        tasksCompleted: change,
        summaries: change,         // spoken summaries played, written fresh or replayed
    }),
    // Of the tasks completed in the period that had a due time, how many were done by it.
    onTime: z.object({ onTime: z.number(), total: z.number() }),
    // Every day of the period, quiet ones too (zeros), oldest first.
    daily: z.array(z.object({
        day: z.string(),
        activePeople: z.number(),
        signUps: z.number(),
        tasksCreated: z.number(),
        tasksCompleted: z.number(),
        summariesWritten: z.number(),      // a model call and a voice: the ones that cost money
        summariesReplayed: z.number(),     // served from memory
    })),
    // Of the people who signed up in the period, how many went on to each next step in it.
    activation: z.object({ signedUp: z.number(), createdBoard: z.number(), createdTask: z.number(), completedTask: z.number(), playedSummary: z.number() }),
    events: z.array(z.object({ eventType: z.string(), count: z.number(), lastSeen: z.string().nullable() })),
    // The most summaries the server writes in a day (BRIEFINGS_PER_DAY), for comparing with `summariesWritten`.
    limits: z.object({ summariesPerDay: z.number().nullable() }),
});
export type Dashboard = z.infer<typeof dashboard>;

export const getDashboard = endpoint({
    method: 'GET',
    path: '/metrics/dashboard',
    query: z.object({ days: z.enum(DASHBOARD_DAYS.map(String) as ['7', '14', '30', '90']).default('30') }),
    response: z.object({ dashboard }),
});
